import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { toJpeg } from 'html-to-image';
import { jsPDF } from 'jspdf';
import PaymentReceiptDocument from '../components/PaymentReceiptDocument';
import PaymentReceiptPreview from '../components/PaymentReceiptPreview';
import ReferenceDropdown from '../components/ReferenceDropdown';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const PAYMENT_METHODS = [ 'UPI', 'Razor Pay','Cash', 'Cheque', 'Bank Transfer', 'Online Payment'];

interface Bill {
  id: number;
  bill_no: number;
  bill_date: string;
  invoice_period_start: string;
  invoice_period_end: string;
  total_amount: number;
  status: string;
  patient_id: number;
}

interface Patient {
  id: number;
  patient_name: string;
  patient_salutation?: string;
  age: string;
  sex: string;
  treating_hospital: string;
  treating_doctor: string;
  address: string;
  patient_clients: { salutation?: string; client_name: string; contact_number_1: string; email: string }[];
}

interface Entry {
  payment_method: string;
  amount: string;
  payment_date: string;
  reference_no: string;
  notes: string;
}

const emptyEntry = (): Entry => ({
  payment_method: 'Cash',
  amount: '',
  payment_date: new Date().toISOString().split('T')[0],
  reference_no: '',
  notes: '',
});

function dmyFmt(d: string): string {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return d;
}

function fmtNum(n: number): string {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const CreatePaymentReceipt: React.FC = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const receiptRef = useRef<HTMLDivElement>(null);

  const [bill, setBill] = useState<Bill | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [entries, setEntries] = useState<Entry[]>([emptyEntry()]);
  const [collectedBy, setCollectedBy] = useState('LHHS Team');
  const [paymentMadeByTitle, setPaymentMadeByTitle] = useState('');
  const [paymentMadeByName, setPaymentMadeByName] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);   // open the receipt preview right after saving
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const load = async () => {
      if (!billId) return;
      try {
        const billRes = await axios.get(`${API_BASE_URL}/api/billing/bills/${billId}`, { headers });
        const b: Bill = billRes.data;
        setBill(b);

        const [patientRes, receiptRes] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/api/patients/${b.patient_id}`, { headers }),
          axios.get(`${API_BASE_URL}/api/billing/bills/${billId}/payment-receipt`, { headers }),
        ]);

        if (patientRes.status === 'fulfilled') {
          const pat: Patient = patientRes.value.data;
          setPatient(pat);
          const defaultClient = pat.patient_clients?.[0];
          const clientName = defaultClient?.client_name || '';
          const clientSalutation = defaultClient?.salutation || '';
          setPaymentMadeByTitle(clientSalutation);
          setPaymentMadeByName(clientName || pat.patient_name);
        }

        if (receiptRes.status === 'fulfilled' && receiptRes.value.data.entries?.length) {
          setIsEdit(true);
          const receiptData = receiptRes.value.data;
          setEntries(
            receiptData.entries.map((e: any) => ({
              payment_method: e.payment_method,
              amount: String(e.amount),
              payment_date: e.payment_date,
              reference_no: e.reference_no || '',
              notes: e.notes || '',
            })),
          );
          setCollectedBy(receiptData.collected_by || 'LHHS Team');
          if (receiptData.payment_made_by) {
            const parts = receiptData.payment_made_by.split(' ');
            setPaymentMadeByTitle(parts[0] || '');
            setPaymentMadeByName(parts.slice(1).join(' ') || receiptData.payment_made_by);
          }
        }
      } catch {
        toast.error('Failed to load bill data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [billId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateEntry = (idx: number, field: keyof Entry, value: string) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
  };

  const addEntry = () => setEntries((prev) => [...prev, emptyEntry()]);

  const removeEntry = (idx: number) => {
    if (entries.length === 1) return;
    setEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const totalReceived = entries.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const balance = bill ? bill.total_amount - totalReceived : 0;

  const handleSave = async () => {
    for (const e of entries) {
      if (!e.amount || isNaN(parseFloat(e.amount)) || parseFloat(e.amount) <= 0) {
        toast.error('Each entry must have a valid amount');
        return;
      }
      if (!e.payment_date) {
        toast.error('Each entry must have a date');
        return;
      }
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/billing/bills/${billId}/payment-receipt`,
        {
          collected_by: collectedBy || null,
          payment_made_by: `${paymentMadeByTitle ? paymentMadeByTitle + ' ' : ''}${paymentMadeByName}`.trim() || null,
          entries: entries.map((e) => ({
            payment_method: e.payment_method,
            amount: parseFloat(e.amount),
            payment_date: e.payment_date,
            reference_no: e.reference_no || null,
            notes: e.notes || null,
          })),
        },
        { headers },
      );

      if (receiptRef.current) {
        const dataUrl = await toJpeg(receiptRef.current, {
          quality: 1.0, pixelRatio: 2, backgroundColor: '#ffffff',
        });
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        pdf.addImage(dataUrl, 'JPEG', 0, 0, 210, 297);
        const file = new File([pdf.output('blob')], `payment_receipt_${bill?.bill_no}.pdf`, { type: 'application/pdf' });
        const fd = new FormData();
        fd.append('pdf_file', file);
        await axios.post(
          `${API_BASE_URL}/api/billing/bills/${billId}/payment-receipt/pdf`,
          fd,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
        );
      }

      toast.success('Payment receipt saved');
      setShowPreview(true);   // open the preview so it can be printed/downloaded right away
    } catch {
      toast.error('Failed to save payment receipt');
    } finally {
      setSaving(false);
    }
  };

  const inp: React.CSSProperties = {
    padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px',
    fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box',
    backgroundColor: 'white',
  };

  const client = patient?.patient_clients?.[0];

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading…</div>;
  }

  if (!bill || !patient) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Failed to load data.</div>;
  }

  return (
    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '1.5rem',
          color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem',
        }}
      >
        ← Back
      </button>

      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' }}>
          {isEdit ? 'Edit Payment Receipt' : 'Create Payment Receipt'}
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
          Bill #{bill.bill_no} · {patient.patient_name}
        </p>
      </div>

      {/* Bill + Patient summary */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.25rem',
      }}>
        {[
          { label: 'Guardian / Client', value: client?.client_name || '—' },
          { label: 'Patient', value: patient.patient_name },
          { label: 'Patient ID', value: `LHHS${patient.id}` },
          { label: 'Contact', value: client?.contact_number_1 || '—' },
          { label: 'Bill No', value: `#${bill.bill_no}` },
          { label: 'Bill Date', value: dmyFmt(bill.bill_date) },
          { label: 'Invoice Period', value: `${dmyFmt(bill.invoice_period_start)} – ${dmyFmt(bill.invoice_period_end)}` },
          { label: 'Bill Amount', value: `₹${fmtNum(bill.total_amount)}` },
        ].map(({ label, value }) => (
          <div key={label}>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {label}
            </div>
            <div style={{ fontSize: '0.88rem', color: '#334155', marginTop: '3px', fontWeight: label === 'Bill Amount' ? 700 : 400 }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Receipt meta */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
      }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', marginBottom: '1rem' }}>
          Receipt Details
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
              Receipt Issue Date
            </label>
            <input
              type="text"
              value={new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              disabled
              style={{ ...inp, backgroundColor: '#f8fafc', color: '#64748b' }}
            />
          </div>
          <div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div style={{ width: '110px', flexShrink: 0 }}>
                <ReferenceDropdown
                  label="Title"
                  category="salutation"
                  value={paymentMadeByTitle}
                  onChange={setPaymentMadeByTitle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  Payment Made By
                </label>
                <input
                  type="text"
                  value={paymentMadeByName}
                  onChange={(e) => setPaymentMadeByName(e.target.value)}
                  placeholder="Full Name"
                  style={inp}
                />
              </div>
            </div>
          </div>
          <div>
            <ReferenceDropdown
              label="Payment Collected By"
              category="payment_collected_by"
              value={collectedBy}
              onChange={setCollectedBy}
            />
          </div>
        </div>
      </div>

      {/* Payment entries */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
        overflow: 'hidden', marginBottom: '1.5rem',
      }}>
        <div style={{
          padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>Payment Entries</span>
          <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Add partial payments as separate rows</span>
        </div>

        <div style={{ padding: '1.25rem 1.5rem' }}>
          <div style={{ width: '80%' }}>
            {/* Column headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 0.8fr 1.4fr 0.9fr 40px',
              gap: '10px', marginBottom: '6px', paddingBottom: '8px',
              borderBottom: '1px solid #e2e8f0',
            }}>
              {['Date', 'Payment Method', 'Reference / UTR / Notes', 'Amount (₹)', ''].map((h) => (
                <span key={h} style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {h}
                </span>
              ))}
            </div>

            {entries.map((entry, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 0.8fr 1.4fr 0.9fr 40px',
                  gap: '10px', marginBottom: '8px', alignItems: 'center',
                  backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white',
                  borderRadius: '8px', padding: '6px 8px',
                  border: '1px solid #f1f5f9',
                }}
              >
                <input
                  type="date" value={entry.payment_date}
                  onChange={(e) => updateEntry(idx, 'payment_date', e.target.value)}
                  style={{ ...inp, fontSize: '0.85rem' }}
                />
                <select value={entry.payment_method} onChange={(e) => updateEntry(idx, 'payment_method', e.target.value)} style={{ ...inp, fontSize: '0.85rem' }}>
                  {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <input
                  type="text" placeholder="UTR / Cheque No / Ref / Notes" value={entry.reference_no}
                  onChange={(e) => updateEntry(idx, 'reference_no', e.target.value)}
                  style={{ ...inp, fontSize: '0.85rem' }}
                />
                <input
                  type="number" placeholder="0.00" value={entry.amount}
                  onChange={(e) => updateEntry(idx, 'amount', e.target.value)}
                  style={{ ...inp, textAlign: 'right', fontSize: '0.85rem', fontWeight: 600 }}
                />
                <button
                  onClick={() => removeEntry(idx)}
                  disabled={entries.length === 1}
                  title="Remove"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '30px', height: '30px', borderRadius: '6px',
                    border: entries.length > 1 ? '1px solid #fecaca' : '1px solid #e2e8f0',
                    background: entries.length > 1 ? '#fef2f2' : '#f8fafc',
                    cursor: entries.length > 1 ? 'pointer' : 'default',
                    color: entries.length > 1 ? '#ef4444' : '#cbd5e1',
                    fontSize: '1rem', padding: 0, lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            ))}

            <button
              onClick={addEntry}
              style={{
                marginTop: '10px', padding: '9px 24px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                border: 'none', borderRadius: '8px',
                cursor: 'pointer', color: 'white', fontSize: '0.85rem',
                fontWeight: 600, letterSpacing: '0.02em',
                boxShadow: '0 2px 6px rgba(37,99,235,0.3)',
              }}
            >
              + Add Payment Entry
            </button>
          </div>

          {/* Summary bar */}
          <div style={{ marginTop: '1.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <div style={{ padding: '12px 16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Total Received</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#15803d', marginTop: '3px' }}>₹{fmtNum(totalReceived)}</div>
            </div>
            <div style={{ padding: '12px 16px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
              <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Bill Amount</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#92400e', marginTop: '3px' }}>₹{fmtNum(bill.total_amount)}</div>
            </div>
            <div style={{
              padding: '12px 16px', borderRadius: '8px',
              backgroundColor: balance <= 0 ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${balance <= 0 ? '#bbf7d0' : '#fecaca'}`,
            }}>
              <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Balance Due</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '3px', color: balance <= 0 ? '#15803d' : '#dc2626' }}>
                ₹{fmtNum(Math.max(balance, 0))}
                {balance <= 0 && <span style={{ fontSize: '0.75rem', marginLeft: '6px' }}>✓ Paid</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '0.65rem 1.4rem', border: '1px solid #e2e8f0', borderRadius: '8px',
            background: 'white', cursor: 'pointer', fontSize: '0.92rem', color: '#475569',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '0.65rem 1.8rem', backgroundColor: '#2563eb', color: 'white',
            border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '0.92rem', fontWeight: 600, opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving & generating PDF…' : 'Save & Generate Receipt PDF'}
        </button>
      </div>

      {/* Hidden receipt for PDF generation — off-screen */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, pointerEvents: 'none' }}>
        <PaymentReceiptDocument
          ref={receiptRef}
          bill={bill}
          patient={patient}
          entries={entries}
          collectedBy={collectedBy || 'LHHS Team'}
          paymentMadeBy={`${paymentMadeByTitle ? paymentMadeByTitle + ' ' : ''}${paymentMadeByName}`.trim() || undefined}
        />
      </div>

      {/* After saving, open the preview so the receipt can be printed/downloaded directly */}
      {showPreview && bill && patient && (
        <PaymentReceiptPreview
          bill={bill as any}
          patient={patient as any}
          onClose={() => { setShowPreview(false); navigate(-1); }}
        />
      )}
    </div>
  );
};

export default CreatePaymentReceipt;
